import React, { useEffect, useMemo, useState } from "react";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import PageHeader from "../../components/ui/PageHeader";
import Switch from "../../components/ui/Switch";
import {
    createPriceTypeRequest,
    getPriceTypesRequest,
    updatePriceTypeRequest,
} from "../../services/api/event-content.api";
import { PriceTypeItem } from "../../services/types/event-content.types";

interface FormState {
    nameRu: string;
    nameEn: string;
    nameDe: string;
    nameUa: string;
    sort: string;
    isActive: boolean;
}

const initialFormState: FormState = {
    nameRu: "",
    nameEn: "",
    nameDe: "",
    nameUa: "",
    sort: "",
    isActive: true,
};

const PriceTypesPage: React.FC = () => {
    const [items, setItems] = useState<PriceTypeItem[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formState, setFormState] = useState<FormState>(initialFormState);
    const [searchValue, setSearchValue] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const selectedItem = useMemo(
        () => items.find((item) => item.id === selectedId) ?? null,
        [items, selectedId],
    );

    const filteredItems = useMemo(() => {
        const query = searchValue.trim().toLowerCase();
        const sorted = [...items].sort((a, b) => {
            const sortA = a.sort ?? 999999;
            const sortB = b.sort ?? 999999;

            if (sortA !== sortB) {
                return sortA - sortB;
            }

            return (a.name_ru ?? "").localeCompare(b.name_ru ?? "", "ru", {
                sensitivity: "base",
            });
        });

        if (!query) {
            return sorted;
        }

        return sorted.filter((item) =>
            [item.name_ru, item.name_en, item.name_de, item.name_ua].some((value) =>
                (value ?? "").toLowerCase().includes(query),
            ),
        );
    }, [items, searchValue]);

    const resetForm = () => {
        setSelectedId(null);
        setFormState(initialFormState);
        setFormError(null);
        setSuccessMessage(null);
    };

    const syncFormFromItem = (item: PriceTypeItem) => {
        setFormState({
            nameRu: item.name_ru ?? "",
            nameEn: item.name_en ?? "",
            nameDe: item.name_de ?? "",
            nameUa: item.name_ua ?? "",
            sort: item.sort != null ? String(item.sort) : "",
            isActive: item.is_active,
        });
    };

    const handleSelect = (item: PriceTypeItem) => {
        setSelectedId(item.id);
        syncFormFromItem(item);
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleChange =
        (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
            setFormState((prev) => ({
                ...prev,
                [field]: event.target.value,
            }));
        };

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const response = await getPriceTypesRequest();
            setItems(Array.isArray(response) ? response : []);
        } catch (error) {
            setPageError(error instanceof Error ? error.message : "Не удалось загрузить типы цен");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
    }, []);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);
        setSuccessMessage(null);

        if (!formState.nameRu.trim() || !formState.nameEn.trim() || !formState.nameDe.trim()) {
            setFormError("Заполните названия на всех трёх языках.");
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                name_ru: formState.nameRu.trim(),
                name_en: formState.nameEn.trim(),
                name_de: formState.nameDe.trim(),
                name_ua: formState.nameUa.trim() || null,
                sort: formState.sort.trim() ? Number(formState.sort) : null,
                is_active: formState.isActive,
            };

            if (selectedId) {
                const updated = await updatePriceTypeRequest(selectedId, payload);
                setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
                setSelectedId(updated.id);
                syncFormFromItem(updated);
                setSuccessMessage("Тип цены успешно обновлён.");
            } else {
                const created = await createPriceTypeRequest(payload);
                setItems((prev) => [created, ...prev]);
                setSelectedId(created.id);
                syncFormFromItem(created);
                setSuccessMessage("Тип цены успешно создан.");
            }
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось сохранить тип цены");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Типы цен"
                description="Управление типами цен в административной панели."
                actions={
                    <Button variant="primary" onClick={resetForm}>
                        Новый тип цены
                    </Button>
                }
            />

            <section className="content-split artist-layout">
                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">Список типов цен</h3>
                            <p className="card-text">
                                Используйте поиск, чтобы быстро найти нужную запись.
                            </p>
                        </div>

                        <div className="section-toolbar-controls">
                            <Input
                                type="text"
                                placeholder="Поиск по названию..."
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />
                        </div>
                    </div>

                    {pageError ? (
                        <div className="form-global-error">{pageError}</div>
                    ) : isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Типы цен не найдены</h3>
                            <p className="empty-block-text">
                                Создайте первый тип цены или измените поисковый запрос.
                            </p>
                        </div>
                    ) : (
                        <div className="table-wrap">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>RU</th>
                                    <th>EN</th>
                                    <th>DE</th>
                                    <th>UA</th>
                                    <th>Sort</th>
                                    <th>Статус</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredItems.map((item) => {
                                    const isSelected = item.id === selectedId;

                                    return (
                                        <tr
                                            key={item.id}
                                            className={isSelected ? "data-table-row-active" : ""}
                                            onClick={() => handleSelect(item)}
                                        >
                                            <td>
                                                <div className="table-primary-cell">
                                                    <span className="table-primary-text">{item.name_ru}</span>
                                                    <span className="table-secondary-text">{item.id}</span>
                                                </div>
                                            </td>
                                            <td>{item.name_en}</td>
                                            <td>{item.name_de}</td>
                                            <td>{item.name_ua || "—"}</td>
                                            <td>{item.sort ?? "—"}</td>
                                            <td>
                          <span
                              className={`status-badge ${
                                  item.is_active
                                      ? "status-badge-active"
                                      : "status-badge-inactive"
                              }`}
                          >
                            {item.is_active ? "Активна" : "Выключена"}
                          </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>


                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">
                                {selectedItem ? "Редактирование типа цены" : "Новый тип цены"}
                            </h3>
                            <p className="card-text">
                                Заполните названия и параметры отображения типа цены.
                            </p>
                        </div>


                        {selectedItem ? (
                            <span className="editor-badge">ID выбран</span>
                        ) : (
                            <span className="editor-badge editor-badge-soft">Создание</span>
                        )}

                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        <div className="form-grid">
                            <Field label="Название RU" htmlFor="price-type-name-ru">
                                <Input
                                    id="price-type-name-ru"
                                    type="text"
                                    placeholder="Например, Стандарт"
                                    value={formState.nameRu}
                                    onChange={handleChange("nameRu")}
                                />
                            </Field>

                            <Field label="Название EN" htmlFor="price-type-name-en">
                                <Input
                                    id="price-type-name-en"
                                    type="text"
                                    placeholder="For example Standard"
                                    value={formState.nameEn}
                                    onChange={handleChange("nameEn")}
                                />
                            </Field>

                            <Field label="Название DE" htmlFor="price-type-name-de">
                                <Input
                                    id="price-type-name-de"
                                    type="text"
                                    placeholder="Zum Beispiel Standard"
                                    value={formState.nameDe}
                                    onChange={handleChange("nameDe")}
                                />
                            </Field>

                            <Field label="Название UA" htmlFor="price-type-name-ua">
                                <Input
                                    id="price-type-name-ua"
                                    type="text"
                                    placeholder="Українською"
                                    value={formState.nameUa}
                                    onChange={handleChange("nameUa")}
                                />
                            </Field>

                            <Field label="Sort" htmlFor="price-type-sort">
                                <Input
                                    id="price-type-sort"
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="10"
                                    value={formState.sort}
                                    onChange={handleChange("sort")}
                                />
                            </Field>
                        </div>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isActive}
                                onChange={(checked) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        isActive: checked,
                                    }))
                                }
                                label={formState.isActive ? "Тип цены активен" : "Тип цены неактивен"}
                            />
                        </div>

                        {formError ? <div className="form-global-error">{formError}</div> : null}
                        {successMessage ? (
                            <div className="form-global-success">{successMessage}</div>
                        ) : null}

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving}>
                                {isSaving
                                    ? "Сохранение..."
                                    : selectedItem
                                        ? "Сохранить изменения"
                                        : "Создать тип цены"}
                            </Button>

                            <Button type="button" variant="secondary" onClick={resetForm} disabled={isSaving}>
                                Очистить форму
                            </Button>
                        </div>
                    </form>
                </Card>
            </section>
        </div>
    );
};

export default PriceTypesPage;
